import { ICalculatePaystubDeductionRes, IGetFixedDataRes, IGetPaystubPayInfoRes } from '~/types/paystub';
import { ReactComponent as PaystubHelpCircleIcon } from 'assets/icons/paystub-help-circle.svg';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

/** 산출식 문자열에서 비율(%) 추출. e.g. "건강보험 X 12.95%" → 0.1295 */
function parseRateFromFormula(formula: string): number | null {
  const match = formula.match(/([\d.]+)\s*%?/);

  if (!match) return null;
  const percent = Number(match[1]);
  return Number.isNaN(percent) ? null : percent / 100;
}

interface IPaystubDeductionTableProps {
  deduction: ICalculatePaystubDeductionRes;
  setDeduction: (deduction: ICalculatePaystubDeductionRes) => void;
  previewInfo: IGetFixedDataRes['resultData'];
}

const PaystubDeductionTable = ({ deduction, setDeduction, previewInfo }: IPaystubDeductionTableProps) => {
  return (
    <div className="mt-12">
      {/* 제목 */}
      <div className="flex items-center justify-start gap-2">
        <p className="mb-0 text-[18px] font-bold leading-[30px] text-[#333]">공제항목</p>
        <div data-tooltip-id="deduction-help-tooltip" style={{ cursor: 'pointer', display: 'flex' }}>
          <PaystubHelpCircleIcon />
        </div>
      </div>
      <div className="mt-4 flex flex-col overflow-hidden rounded-lg border border-[#e1e1e1]">
        {/* Header */}
        <div className="flex w-full items-center border-b border-[#e1e1e1] bg-[#f2f2f2]">
          <div className="flex h-12 w-[60px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#505054]">구분</p>
          </div>
          <div className="flex h-12 w-[140px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#505054]">항목</p>
          </div>
          <div className="flex h-12 w-[240px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#505054]">산출식</p>
          </div>
          <div className="flex h-12 flex-1 items-center justify-center">
            <p className="text-sm font-bold text-[#505054]">입력</p>
          </div>
        </div>

        {/* Body */}
        <div className="flex w-full bg-white">
          {/* Left Category */}
          <div className="flex w-[60px] items-center justify-center rounded-bl-lg border-r border-[#e1e1e1] px-2 py-4">
            <p className="text-center text-sm font-bold leading-5 text-[#505054]">공제</p>
          </div>

          {/* Items Column */}
          <div className="flex flex-col border-r border-[#e1e1e1]">
            {deduction.deduction_items
              .filter((item) => item.edit_type !== 'CUSTOM')
              .map((item, index) => (
                <div
                  key={index}
                  className={`flex h-[40px] w-[140px] items-center justify-center px-2 py-4 ${
                    index < deduction.deduction_items.filter((item) => item.edit_type !== 'CUSTOM').length - 1
                      ? 'border-b border-[#e1e1e1]'
                      : ''
                  }`}>
                  <p className="text-sm font-bold text-[#505054]">{item.name}</p>
                </div>
              ))}
          </div>

          {/* Formula Column */}
          <div className="flex flex-col border-r border-[#e1e1e1]">
            {deduction.deduction_items
              .filter((item) => item.edit_type !== 'CUSTOM')
              .map((item, index) => (
                <div
                  key={index}
                  className={`flex h-[40px] w-[240px] items-center justify-center px-2 py-4 ${
                    index < deduction.deduction_items.filter((item) => item.edit_type !== 'CUSTOM').length - 1
                      ? 'border-b border-[#e1e1e1]'
                      : ''
                  }`}>
                  <p className="text-center text-xs text-[#868686]">{item.formula}</p>
                </div>
              ))}
          </div>

          {/* Input Column */}
          <div className="flex flex-1 flex-col">
            {deduction.deduction_items
              .filter((item) => item.edit_type !== 'CUSTOM')
              .map((item, index) => {
                return (
                  <div
                    key={index}
                    className={`flex h-[40px] w-full items-center px-2.5 py-4 ${
                      index < deduction.deduction_items.filter((item) => item.edit_type !== 'CUSTOM').length - 1
                        ? 'border-b border-[#e1e1e1]'
                        : ''
                    }`}>
                    <div
                      className={`flex h-[30px] flex-1 items-center justify-end rounded border border-[#e1e1e1] bg-white px-2 py-2.5`}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={item.pay_value}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/[^0-9]/g, '');
                          const inputValue = Number(numericValue);
                          const isHealthInsurance = item.name === '건강보험';

                          // 건강보험 변경 시 장기요양보험 산출식에서 비율 추출 후 자동 계산
                          if (isHealthInsurance) {
                            const longTermCareItem = deduction.deduction_items.find((d) => d.name === '장기요양보험');
                            const rate = longTermCareItem?.formula
                              ? parseRateFromFormula(longTermCareItem.formula)
                              : null;
                            const longTermCareValue = rate != null ? Math.floor(inputValue * rate) : 0;

                            setDeduction({
                              ...deduction,
                              deduction_items: deduction.deduction_items.map((deductionItem) => {
                                if (deductionItem.name === '건강보험') {
                                  return {
                                    ...deductionItem,
                                    pay_value: inputValue,
                                    value: inputValue.toString() + '원',
                                  };
                                }
                                if (deductionItem.name === '장기요양보험') {
                                  return {
                                    ...deductionItem,
                                    pay_value: longTermCareValue,
                                    value: longTermCareValue.toString() + '원',
                                  };
                                }
                                return deductionItem;
                              }),
                            });
                          } else {
                            // 일반 항목은 해당 항목만 업데이트
                            setDeduction({
                              ...deduction,
                              deduction_items: deduction.deduction_items.map((deductionItem) =>
                                deductionItem.name === item.name
                                  ? {
                                      ...deductionItem,
                                      pay_value: inputValue,
                                      value: inputValue.toString() + '원',
                                    }
                                  : deductionItem,
                              ),
                            });
                          }
                        }}
                        className={`w-full bg-transparent text-right text-xs leading-[18px] text-[#505054] outline-none placeholder:text-[#b8b8b8]`}
                        placeholder="0"
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Tooltip */}
      <Tooltip
        id="deduction-help-tooltip"
        place="bottom"
        opacity={1}
        style={{
          backgroundColor: 'white',
          color: '#404040',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          borderRadius: '8px',
          padding: '16px',
          maxWidth: '543px',
          zIndex: 9999,
        }}>
        <div className="flex flex-col gap-3">
          {previewInfo &&
            previewInfo.map((info, index) => (
              <div key={index} className="flex flex-col gap-[3px]">
                <p className="text-xs font-bold leading-[1.4] text-[#404040]">{info.type}</p>
                <div className="flex items-start gap-[3px]">
                  <div className="h-[17px] w-[10px] flex-shrink-0">
                    <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="5" cy="5" r="1.5" fill="#404040" />
                    </svg>
                  </div>
                  <p className="text-xs leading-[1.4] text-[#404040]" style={{ whiteSpace: 'normal' }}>
                    {info.contents}
                  </p>
                </div>
              </div>
            ))}
        </div>
      </Tooltip>
    </div>
  );
};

export default PaystubDeductionTable;
